import * as React from 'react';

import {
  enumUnit,
  PurchaseLineItemType,
  Unit,
} from '@rematter/frontend-lib-api-client';
import { zeroWeight } from '@rematter/modules/trade/utils/weight';
import { TouchButton } from '@rematter/ui-web';
import { useAtomValue, useSetAtom } from 'jotai';
import { LucidePackage } from 'lucide-react';
import { v4 as uuidv4 } from 'uuid';

import { InventoryItemsModal } from '../../../_shared/InventoryItemsModal';
import {
  $itemsInterfaceHighlightedMaterialIds,
  $itemsInterfaceHighlightedSectionTitle,
  $itemsInterfaceSuggestedPrices,
  resolveSuggestedPrice,
} from '../_state';
import { NormalizedLineItem } from '../_types';

import { $inventoryItemsById } from '~/modules/trade/_state/loadForm.state';
import { $setAutoFocusRowId } from '~/modules/trade/_state/materialsInterface.state';
import { useTranslations } from '~/modules/trade/i18n/useTranslations';
import { enumLoadWeightType } from '~/modules/trade/types/load';
import { useUIPreferences } from '~/shared/hooks';

const ICON_PACKAGE = <LucidePackage />;

type Props = {
  onAddItem?: (
    type: PurchaseLineItemType,
    data?: Partial<NormalizedLineItem>,
  ) => void;
  modalStyle?: React.CSSProperties;
  buttonSize?: 'sm' | 'md' | 'lg' | 'xl';
};

// ⚛️ ⚛️ ⚛️ ADD RAW MATERIAL WIDGET ⚛️ ⚛️ ⚛️
export const AddRawMaterialWidget: React.FC<Props> = ({
  onAddItem,
  modalStyle,
  buttonSize,
}) => {
  // Translations
  const t = useTranslations();

  // States
  const [inventoryModalOpen, setInventoryModalOpen] = React.useState(false);

  // Atoms
  const setAutoFocusRowId = useSetAtom($setAutoFocusRowId);
  const highlightedMaterialIds = useAtomValue(
    $itemsInterfaceHighlightedMaterialIds,
  );
  const highlightedSectionTitle = useAtomValue(
    $itemsInterfaceHighlightedSectionTitle,
  );
  const suggestedPrices = useAtomValue($itemsInterfaceSuggestedPrices);
  const inventoryItemsById = useAtomValue($inventoryItemsById);

  // Data
  const { touchModeEnabled } = useUIPreferences();

  // Callbacks
  const handleAddRawMaterial = React.useCallback(
    (materialId?: string) => {
      if (!onAddItem) return;
      const newItemId = `temp-${uuidv4()}`;
      const suggested = resolveSuggestedPrice(suggestedPrices, materialId);
      const item = materialId ? inventoryItemsById[materialId] : undefined;
      const unit = (item?.effectivePricingUnit as Unit) ?? enumUnit.Pounds;
      onAddItem('Raw', {
        id: newItemId,
        itemId: materialId ?? null,
        weights: {
          [enumLoadWeightType.Gross]: zeroWeight(unit),
          [enumLoadWeightType.Tare]: zeroWeight(unit),
          [enumLoadWeightType.Net]: zeroWeight(unit),
        },
        unitPrice: suggested?.unitPrice ?? null,
        pricingUnit: unit,
      });
      setAutoFocusRowId(newItemId);
    },
    [onAddItem, setAutoFocusRowId, suggestedPrices],
  );

  const handlePress = React.useCallback(() => {
    if (touchModeEnabled) {
      setInventoryModalOpen(true);
    } else {
      handleAddRawMaterial();
    }
  }, [touchModeEnabled, handleAddRawMaterial]);

  const handleModalOpenChange = React.useCallback((isOpen: boolean) => {
    if (!isOpen) setInventoryModalOpen(false);
  }, []);

  return (
    <>
      <TouchButton
        onPress={handlePress}
        className="@AddItemButton w-full"
        isSelected={false}
        leftElement={ICON_PACKAGE}
        color="primary"
        variant="outline"
        size={buttonSize}
        aria-label="Add Item"
      >
        {t.scaleTicketPro.actions.addMaterial}
      </TouchButton>

      <InventoryItemsModal
        isOpen={inventoryModalOpen}
        mode="items"
        onOpenChange={handleModalOpenChange}
        onSelectMaterial={handleAddRawMaterial}
        modalStyle={modalStyle}
        highlightedMaterialIds={highlightedMaterialIds}
        highlightedSectionTitle={highlightedSectionTitle}
      />
    </>
  );
};
